import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";

import {
  addInternationalOurServicesDetails,
  fetchInternationalOurServicesDetails,
  editInternationalOurServicesDetails,
  updateInternationalOurServicesDetails,
  trashInternationalOurServicesDetails,
  addInternationalOurServicesCategory,
  fetchInternationalOurServicesCategory,
  editInternationalOurServicesCategory,
  updateInternationalOurServicesCategory,
  trashInternationalOurServicesCategory,
} from "../../Adapters/ApiAdapter";

import { baseImage_path } from "../../variables/Variables";
import { Add } from "@mui/icons-material";

const InternationalOurServices = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category_title: "",
    heading: "",
    img_icon: null,
    service_category_id: "",
    existing_icon: "",
  });

  const [openCategory, setOpenCategory] = useState(false);
  const [categoryEditId, setCategoryEditId] = useState(null);
  const [categoryTitle, setCategoryTitle] = useState("");

  const fetchData = async () => {
    const serviceData = await fetchInternationalOurServicesDetails();
    const categoryData = await fetchInternationalOurServicesCategory();
    setServices(serviceData?.data || []);
    setCategories(categoryData?.data || []);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpen = () => {
    setIsEditMode(false);
    setFormData({
      category_title: "",
      heading: "",
      img_icon: null,
      service_category_id: "",
      existing_icon: "",
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setIsEditMode(false);
  };

  const handleEdit = async (id) => {
    const res = await editInternationalOurServicesDetails(id);
    if (res?.status === "true") {
      setFormData({
        category_title: res.data.category_title,
        heading: res.data.heading,
        img_icon: null,
        service_category_id: res.data.service_category_id,
        existing_icon: res.data.img_icon,
      });
      setEditId(id);
      setIsEditMode(true);
      setOpen(true);
    }
  };

  const handleSubmit = async () => {
    const payload = new FormData();
    payload.append("category_title", formData.category_title);
    payload.append("heading", formData.heading);
    payload.append("service_category_id", formData.service_category_id);
    if (formData.img_icon) payload.append("img_icon", formData.img_icon);
    try {
      if (isEditMode) {
        const response = await updateInternationalOurServicesDetails(
          editId,
          payload
        );
        if (response.status === "true")
          toast.success("Service updated successfully");
      } else {
        const response = await addInternationalOurServicesDetails(payload);
        if (response.status === "true")
          toast.success("Service added successfully");
      }
      handleClose();
      fetchData();
    } catch (err) {
      console.error("Error submitting data", err);
    }
  };

  const handleTrash = async (id) => {
    try {
      const res = await trashInternationalOurServicesDetails(id);
      if (res?.status === "true") {
        toast.success("Service deleted successfully");
        fetchData();
      } else {
        toast.error("Failed to delete service");
      }
    } catch (err) {
      console.error("Error deleting item", err);
    }
  };

  const handleCategorySubmit = async () => {
    try {
      if (categoryEditId) {
        const payload = new FormData();
        payload.append("title", categoryTitle);
        const res = await updateInternationalOurServicesCategory(
          categoryEditId,
          payload
        );
        if (res?.status === "true")
          toast.success("Category updated successfully");
      } else {
        const payload = new FormData();
        payload.append("title", categoryTitle);
        const res = await addInternationalOurServicesCategory(payload);
        if (res?.status === "true")
          toast.success("Category added successfully");
      }
      setCategoryEditId(null);
      setCategoryTitle("");
      setOpenCategory(false);
      fetchData();
    } catch (err) {
      console.error("Error submitting category", err);
    }
  };

  const handleCategoryEdit = async (id) => {
    const res = await editInternationalOurServicesCategory(id);
    if (res?.status === "true") {
      setCategoryEditId(id);
      setCategoryTitle(res.data.title);
      setOpenCategory(true);
    }
  };

  const handleCategoryTrash = async (id) => {
    try {
      const res = await trashInternationalOurServicesCategory(id);
      if (res?.status === "true") {
        toast.success("Category deleted successfully");
        fetchData();
      }
    } catch (err) {
      console.error("Error deleting category", err);
    }
  };

  const serviceColumns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "category_title", headerName: "Category Title", flex: 1 },
    { field: "heading", headerName: "Heading", flex: 1 },
    {
      field: "img_icon",
      headerName: "Image",
      renderCell: (params) => (
        <img
          src={`${baseImage_path}/${params.row.img_icon}`}
          alt="icon"
          style={{ width: 50, height: 50 }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            sx={{ ml: 1 }}
            onClick={() => handleTrash(params.row.id)}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  const categoryColumns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "title", headerName: "Category Title", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleCategoryEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            sx={{ ml: 1 }}
            onClick={() => handleCategoryTrash(params.row.id)}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box p={3}>
      <ToastContainer />
      <Typography variant="h5" gutterBottom>
        International Our Services
      </Typography>

      <Box mt={5}>
        <Typography variant="h6" gutterBottom>
          Service Categories
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenCategory(true)}
          startIcon={<Add />}
        >
          Add Category
        </Button>
        <Box mt={2}>
          <DataGrid
            rows={categories}
            columns={categoryColumns}
            getRowId={(row) => row.id}
            autoHeight
          />
        </Box>
      </Box>

      <Box mt={5}>
        <Typography variant="h6" gutterBottom>
          Service Details
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpen}
          startIcon={<Add />}
        >
          Add Service Details
        </Button>
        <Box mt={3}>
          <DataGrid
            rows={services}
            columns={serviceColumns}
            getRowId={(row) => row.id}
            autoHeight
          />
        </Box>
      </Box>
      {/* Service Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{isEditMode ? "Edit" : "Add"} Service</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Heading"
            value={formData.heading}
            onChange={(e) =>
              setFormData({ ...formData, heading: e.target.value })
            }
            margin="normal"
          />

          <TextField
            select
            fullWidth
            label="Select Category"
            value={formData.service_category_id}
            onChange={(e) =>
              setFormData({ ...formData, service_category_id: e.target.value })
            }
            margin="normal"
          >
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.title}
              </MenuItem>
            ))}
          </TextField>

          <Button variant="contained" component="label" sx={{ mt: 2 }}>
            Upload Icon
            <input
              type="file"
              hidden
              onChange={(e) =>
                setFormData({ ...formData, img_icon: e.target.files[0] })
              }
            />
          </Button>

          {(formData.img_icon || formData.existing_icon) && (
            <Box mt={2}>
              <Typography variant="subtitle2">Icon Preview:</Typography>
              <img
                src={
                  formData.img_icon
                    ? URL.createObjectURL(formData.img_icon)
                    : `${baseImage_path}/${formData.existing_icon}`
                }
                alt="Preview"
                style={{ width: 100, height: 100, objectFit: "cover" }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            style={{ fontWeight: "bold", backgroundColor: "grey" }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditMode ? "Save" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Category Dialog */}
      <Dialog
        open={openCategory}
        onClose={() => setOpenCategory(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{categoryEditId ? "Edit" : "Add"} Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category Title"
            value={categoryTitle}
            onChange={(e) => setCategoryTitle(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenCategory(false)}
            style={{ fontWeight: "bold", backgroundColor: "grey" }}
          >
            Cancel
          </Button>
          <Button onClick={handleCategorySubmit} variant="contained">
            {categoryEditId ? "Save" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InternationalOurServices;
